import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Moon, Sun, Loader2, Check, ShieldCheck, Users, DollarSign, Save, Home, ChevronRight, ChevronDown, Coins, AlertTriangle, ScrollText } from "lucide-react";
import { toast } from "sonner";
import { useTheme } from "../context/ThemeContext";
import { useAuth } from "../context/AuthContext";
import { useSafeBack } from "../hooks/useSafeBack";
import { api, formatApiError } from "../lib/api";
import { PlanSelector } from "../components/PlanSelector";
import { StripeCheckoutSheet } from "../components/StripeCheckoutSheet";
import { BuyCreditsSheet } from "../components/BuyCreditsSheet";
import { BottomSheet } from "../components/BottomSheet";
import { LegalContent } from "../lib/legalRender";
import { MfaSetupForm, BackupCodes, NewPasswordForm } from "../components/AuthChallenge";

export default function SettingsPage() {
  const navigate = useNavigate();
  const goBack = useSafeBack("/profile");
  const { theme, toggle } = useTheme();
  const { user, family, refreshUser } = useAuth();
  const [tab, setTab] = useState("general");
  const isAdmin = family?.role === "admin" || family?.role === "organizer";

  const tabs = [
    { key: "general", label: "General" },
    { key: "subscription", label: "Subscription" },
    { key: "vault_costs", label: "Vault Costs" },
    { key: "about", label: "About" },
    ...(isAdmin ? [{ key: "admin", label: "Admin" }] : []),
  ];

  return (
    <div style={{ paddingBottom: 40 }}>
      <div className="sticky top-0 z-40 glass hairline px-5 py-4 lg:px-8 lg:py-6 flex items-center gap-3">
        <button onClick={goBack} data-testid="settings-back-btn"><ArrowLeft size={22} /></button>
        <h1 className="font-serif-display text-2xl lg:text-4xl">Settings</h1>
      </div>

      <div className="px-5 pt-4 lg:px-8 lg:pt-8">
        <div className="flex gap-2 p-1 rounded-2xl" style={{ background: "var(--overlay)" }}>
          {tabs.map((t) => (
            <button key={t.key} onClick={() => setTab(t.key)} data-testid={`settings-tab-${t.key}`}
              className="flex-1 rounded-xl py-2.5 text-sm font-medium transition-colors relative">
              {tab === t.key && (
                <div className="absolute inset-0 rounded-xl" style={{ background: "var(--accent)" }} />
              )}
              <span className="relative" style={{ color: tab === t.key ? "#fff" : "var(--text-secondary)" }}>{t.label}</span>
            </button>
          ))}
        </div>
      </div>

      {tab === "general" && (
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="px-5 py-5 lg:px-8 lg:py-7">
          <div className="text-xs uppercase tracking-widest mb-2" style={{ color: "var(--text-muted)" }}>Appearance</div>
          <div className="card-surface rounded-2xl p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-xl grid place-items-center" style={{ background: "var(--overlay)" }}>
                {theme === "dark" ? <Moon size={18} className="gold-text" /> : <Sun size={18} className="gold-text" />}
              </div>
              <div>
                <div className="text-sm font-semibold">{theme === "dark" ? "Dark mode" : "Light mode"}</div>
                <div className="text-[11px]" style={{ color: "var(--text-muted)" }}>Switch the app theme</div>
              </div>
            </div>
            <button onClick={toggle} data-testid="theme-toggle" className="relative h-7 w-12 rounded-full transition-colors"
              style={{ background: theme === "dark" ? "var(--overlay)" : "var(--accent)" }} aria-label="Toggle theme">
              <span className="absolute top-1 h-5 w-5 rounded-full" style={{ background: "#fff", left: theme === "dark" ? 4 : 24, transition: "left 0.2s ease" }} />
            </button>
          </div>

          {user?.family_count > 1 && <FamilySwitcher />}
          <SecurityPanel />
        </motion.div>
      )}

      {tab === "subscription" && <SubscriptionPanel user={user} family={family} refreshUser={refreshUser} />}
      {tab === "vault_costs" && <VaultCostsPanel />}
      {tab === "about" && <AboutPanel />}
      {tab === "admin" && isAdmin && <AdminPanel refreshUser={refreshUser} />}
    </div>
  );
}

function LegalToggle({ icon: Icon, title, content, defaultOpen = true, testid }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="card-surface rounded-2xl overflow-hidden" data-testid={testid}>
      <button onClick={() => setOpen((o) => !o)} data-testid={`${testid}-toggle`}
        className="w-full flex items-center justify-between gap-3 p-4 text-left">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-xl grid place-items-center shrink-0" style={{ background: "var(--overlay)" }}>
            <Icon size={18} className="gold-text" />
          </div>
          <div className="text-sm font-semibold">{title}</div>
        </div>
        {open ? <ChevronDown size={18} style={{ color: "var(--text-muted)" }} />
              : <ChevronRight size={18} style={{ color: "var(--text-muted)" }} />}
      </button>
      {open && (
        <div className="px-4 pb-5 pt-1 border-t" style={{ borderColor: "var(--hairline)" }} data-testid={`${testid}-content`}>
          {content
            ? <LegalContent className="vault-md legal-md text-sm mt-3" content={content} style={{ color: "var(--text-secondary)" }} />
            : <div className="text-sm mt-3" style={{ color: "var(--text-muted)" }}>This document hasn't been published yet.</div>}
        </div>
      )}
    </div>
  );
}

function SecurityPanel() {
  const { user, refreshUser } = useAuth();
  const [step, setStep] = useState("idle"); // idle | setup | backup | disable
  const [setupData, setSetupData] = useState(null);
  const [backupCodes, setBackupCodes] = useState([]);
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const enabled = !!user?.mfa_enabled;
  const isAdmin = !!user?.is_super_admin;

  const startEnable = async () => {
    setError(""); setLoading(true);
    try { const { data } = await api.post("/auth/mfa/init"); setSetupData(data); setStep("setup"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  const confirmEnable = async (code) => {
    setError(""); setLoading(true);
    try { const { data } = await api.post("/auth/mfa/confirm", { code }); setBackupCodes(data.backup_codes || []); setStep("backup"); }
    catch (e) { setError(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  const finishEnable = async () => { await refreshUser(); setStep("idle"); toast.success("Two-factor authentication is on."); };
  const [pwStep, setPwStep] = useState("idle"); // idle | change
  const [curPw, setCurPw] = useState("");
  const [pwErr, setPwErr] = useState("");
  const [pwLoading, setPwLoading] = useState(false);
  const changePassword = async (newPw) => {
    setPwErr(""); setPwLoading(true);
    try {
      await api.post("/auth/change-password", { current_password: curPw, new_password: newPw });
      setPwStep("idle"); setCurPw(""); toast.success("Password updated.");
    } catch (e) { setPwErr(formatApiError(e.response?.data?.detail)); }
    finally { setPwLoading(false); }
  };
  const doDisable = async () => {
    setError(""); setLoading(true);
    try { await api.post("/auth/mfa/disable", { password }); setPassword(""); setStep("idle"); await refreshUser(); toast.success("Two-factor authentication turned off."); }
    catch (e) { setError(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  return (
    <div className="mt-6" data-testid="security-panel">
      <div className="text-xs uppercase tracking-widest mb-2" style={{ color: "var(--text-muted)" }}>Security</div>
      <div className="card-surface rounded-2xl p-4">
        {step === "idle" && (
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-xl grid place-items-center" style={{ background: "var(--overlay)" }}>
                <ShieldCheck size={18} className="gold-text" />
              </div>
              <div>
                <div className="text-sm font-semibold">Two-factor authentication</div>
                <div className="text-[11px]" style={{ color: "var(--text-muted)" }}>
                  {enabled ? "On · authenticator app required at login" : "Add a code from an authenticator app at login"}
                </div>
              </div>
            </div>
            {enabled
              ? (isAdmin
                  ? <span className="text-[11px] px-2 py-1 rounded-lg" style={{ background: "var(--overlay)", color: "var(--text-muted)" }} data-testid="mfa-required-badge">Required</span>
                  : <button onClick={() => { setError(""); setStep("disable"); }} data-testid="mfa-disable-btn" className="text-xs rounded-lg px-3 py-2" style={{ background: "var(--overlay)", color: "var(--text-secondary)" }}>Turn off</button>)
              : <button onClick={startEnable} disabled={loading} data-testid="mfa-enable-btn" className="text-xs font-semibold rounded-lg px-3 py-2" style={{ background: "var(--accent)", color: "#fff" }}>{loading ? "…" : "Enable"}</button>}
          </div>
        )}
        {step === "setup" && <MfaSetupForm setup={setupData} onSubmit={confirmEnable} error={error} loading={loading} />}
        {step === "backup" && <BackupCodes codes={backupCodes} onDone={finishEnable} />}
        {step === "disable" && (
          <div className="space-y-3" data-testid="mfa-disable-form">
            <div className="text-sm font-semibold">Turn off two-factor</div>
            <p className="text-sm" style={{ color: "var(--text-secondary)" }}>Confirm your password to disable it.</p>
            <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="mfa-disable-password"
              placeholder="Password" className="w-full rounded-xl px-4 py-3 text-sm outline-none"
              style={{ background: "var(--bg-surface)", border: "1px solid var(--border)", color: "var(--text-primary)", fontSize: 16 }} />
            {error && <p className="text-xs" style={{ color: "#e06b70" }}>{error}</p>}
            <div className="flex gap-2">
              <button onClick={() => { setStep("idle"); setPassword(""); setError(""); }} className="flex-1 rounded-xl py-2.5 text-sm" style={{ background: "var(--overlay)", color: "var(--text-secondary)" }}>Cancel</button>
              <button onClick={doDisable} disabled={loading} data-testid="mfa-disable-confirm" className="flex-1 rounded-xl py-2.5 text-sm font-semibold" style={{ background: "#e0555f", color: "#fff", opacity: loading ? 0.6 : 1 }}>{loading ? "…" : "Turn off"}</button>
            </div>
          </div>
        )}
      </div>

      <div className="card-surface rounded-2xl p-4 mt-3">
        {pwStep === "idle" ? (
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-xl grid place-items-center" style={{ background: "var(--overlay)" }}>
                <ShieldCheck size={18} className="gold-text" />
              </div>
              <div>
                <div className="text-sm font-semibold">Password</div>
                <div className="text-[11px]" style={{ color: "var(--text-muted)" }}>Change your account password</div>
              </div>
            </div>
            <button onClick={() => { setPwErr(""); setCurPw(""); setPwStep("change"); }} data-testid="change-password-btn" className="text-xs rounded-lg px-3 py-2" style={{ background: "var(--overlay)", color: "var(--text-secondary)" }}>Change</button>
          </div>
        ) : (
          <div className="space-y-3" data-testid="change-password-form">
            <div className="text-sm font-semibold">Change password</div>
            <input type="password" value={curPw} onChange={(e) => setCurPw(e.target.value)} data-testid="current-password-input"
              placeholder="Current password" autoComplete="current-password"
              className="w-full rounded-xl px-4 py-3.5 text-sm outline-none" style={{ background: "var(--bg-surface)", border: "1px solid var(--border)", color: "var(--text-primary)", fontSize: 16 }} />
            <NewPasswordForm onSubmit={changePassword} error={pwErr} loading={pwLoading} title="New password" submitLabel="Update password" />
            <button onClick={() => { setPwStep("idle"); setCurPw(""); setPwErr(""); }} className="w-full text-center text-xs" style={{ color: "var(--text-secondary)" }}>Cancel</button>
          </div>
        )}
      </div>
    </div>
  );
}

function AboutPanel() {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/legal").then((r) => setData(r.data)).catch(() => setData({ privacy_policy: "", terms_of_use: "" }));
  }, []);
  if (!data) return <div className="grid place-items-center py-16"><Loader2 className="animate-spin gold-text" /></div>;
  const updated = data.updated_at ? new Date(data.updated_at) : null;
  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="px-5 py-5 lg:px-8 lg:py-7 space-y-3" data-testid="about-panel">
      <div className="text-xs uppercase tracking-widest mb-1" style={{ color: "var(--text-muted)" }}>Legal</div>
      <LegalToggle icon={ScrollText} title="Terms of Service" content={data.terms_of_use} testid="about-terms" />
      <LegalToggle icon={ShieldCheck} title="Privacy Policy" content={data.privacy_policy} testid="about-privacy" />
      {updated && (
        <div className="text-[11px] pt-1" style={{ color: "var(--text-muted)" }}>
          Last updated {updated.toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" })}
        </div>
      )}
    </motion.div>
  );
}

function VaultCostsPanel() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/vault-costs").then((r) => setData(r.data)).catch(() => {}); }, []);
  if (!data) return <div className="grid place-items-center py-16"><Loader2 className="animate-spin gold-text" /></div>;
  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="px-5 py-5 lg:px-8 lg:py-7" data-testid="vault-costs-panel">
      <div className="card-surface rounded-2xl p-4 mb-4 flex items-center justify-between">
        <div>
          <div className="text-[11px] uppercase tracking-widest" style={{ color: "var(--text-muted)" }}>AI credits</div>
          <div className="font-serif-display text-2xl gold-text">{data.available_credits} available</div>
          <div className="text-xs" style={{ color: "var(--text-muted)" }}>{data.monthly_credits} included each month · <span className="capitalize">{data.tier}</span></div>
        </div>
        <Coins size={22} className="gold-text" />
      </div>

      {data.low && (
        <div className="rounded-2xl p-4 mb-4" style={{ background: "color-mix(in srgb, #e0a020 16%, transparent)", border: "1px solid color-mix(in srgb, #e0a020 45%, transparent)" }} data-testid="low-credit-banner">
          <div className="flex items-center gap-2 mb-1" style={{ color: "#e0a020" }}>
            <AlertTriangle size={16} /><span className="text-sm font-bold">You're running low on AI credits</span>
          </div>
          <p className="text-xs mb-3" style={{ color: "var(--text-secondary)" }}>You have {data.available_credits} credit{data.available_credits === 1 ? "" : "s"} left (we nudge you below {data.low_threshold}). Top up to keep using the AI vaults.</p>
          <button onClick={() => window.dispatchEvent(new CustomEvent("fv:open-credits"))} data-testid="low-credit-topup"
            className="w-full rounded-xl py-2.5 font-semibold text-sm" style={{ background: "var(--accent)", color: "var(--bg-base)" }}>Top up credits</button>
        </div>
      )}

      <div className="text-xs uppercase tracking-widest mb-2" style={{ color: "var(--text-muted)" }}>What your vaults cost per use</div>
      <div className="space-y-2">
        {data.costs.map((c) => (
          <div key={c.key} className="card-surface rounded-2xl p-4 flex items-center justify-between gap-3" data-testid={`vault-cost-${c.key}`}>
            <div className="min-w-0">
              <div className="text-sm font-semibold" style={{ color: "var(--text-primary)" }}>{c.name}</div>
              {c.note && <div className="text-[11px] mt-0.5" style={{ color: "var(--text-muted)" }}>{c.note}</div>}
            </div>
            <div className="shrink-0 rounded-full px-3 py-1.5 text-sm font-bold gold-text" style={{ background: "color-mix(in srgb, var(--accent) 14%, transparent)", border: "1px solid color-mix(in srgb, var(--accent) 35%, transparent)" }}>
              {c.credits} {c.credits === 1 ? "credit" : "credits"}
            </div>
          </div>
        ))}
      </div>
      <p className="text-[11px] mt-4" style={{ color: "var(--text-muted)" }}>Only the vaults included in your plan are shown. Credits are consumed only when an AI generation runs; plan credits reset each month, purchased packs never expire.</p>
      <BuyCreditsSheet />
    </motion.div>
  );
}

function FamilySwitcher() {
  const { family, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [families, setFamilies] = useState([]);
  const [busy, setBusy] = useState(null);

  const load = async () => { try { const { data } = await api.get("/families"); setFamilies(data); } catch {} };
  useEffect(() => { if (open) load(); }, [open]);

  const choose = async (f) => {
    if (f.active) { setOpen(false); return; }
    setBusy(f.id);
    try {
      await api.post("/families/switch", { family_id: f.id });
      sessionStorage.setItem("fv_tenant_confirmed", "1");
      await refreshUser();
      toast.success(`Switched to ${f.name}`);
      setOpen(false); navigate("/lineage");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(null); }
  };

  return (
    <>
      <div className="text-xs uppercase tracking-widest mb-2 mt-6" style={{ color: "var(--text-muted)" }}>Your families</div>
      <button onClick={() => setOpen(true)} data-testid="switch-families-btn"
        className="card-surface rounded-2xl p-4 flex items-center justify-between w-full text-left">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-xl grid place-items-center" style={{ background: "var(--overlay)" }}>
            <Users size={18} className="gold-text" />
          </div>
          <div>
            <div className="text-sm font-semibold">Switch Families</div>
            <div className="text-[11px]" style={{ color: "var(--text-muted)" }}>Currently in {family?.name}</div>
          </div>
        </div>
        <ChevronRight size={18} style={{ color: "var(--text-muted)" }} />
      </button>

      <BottomSheet open={open} onClose={() => setOpen(false)} title="Switch Families" height="60dvh">
        <div className="space-y-3" data-testid="switch-families-sheet">
          {families.map((f) => (
            <button key={f.id} onClick={() => choose(f)} disabled={!!busy} data-testid={`switch-family-${f.id}`}
              className="w-full card-surface rounded-2xl p-4 flex items-center justify-between text-left"
              style={f.active ? { border: "2px solid var(--accent)" } : {}}>
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-xl grid place-items-center gold-border" style={{ background: "var(--bg-elevated)" }}>
                  <Home size={18} className="gold-text" />
                </div>
                <div>
                  <div className="font-semibold">{f.name}</div>
                  <div className="text-[11px] capitalize" style={{ color: "var(--text-muted)" }}>{f.role}{f.active ? " · current" : ""}</div>
                </div>
              </div>
              {busy === f.id ? <Loader2 size={18} className="animate-spin gold-text" /> : f.active ? <Check size={18} className="gold-text" /> : <ChevronRight size={18} style={{ color: "var(--text-muted)" }} />}
            </button>
          ))}
        </div>
      </BottomSheet>
    </>
  );
}

function SubscriptionPanel({ user, family, refreshUser }) {
  const currentTier = family?.tier || user?.subscription_tier || "roots";
  const [submitting, setSubmitting] = useState(false);
  const [checkout, setCheckout] = useState(null);

  const select = async (tier, billing) => {
    if (tier === currentTier) { toast("That's already your current plan"); return; }
    setSubmitting(true);
    try {
      const { data } = await api.post("/subscription/checkout", { tier, billing, origin_url: window.location.origin });
      if (data.status === "active") { toast.success("Switched to Roots"); await refreshUser(); return; }
      setCheckout({ client_secret: data.client_secret, session_id: data.session_id });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSubmitting(false); }
  };

  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="px-5 py-5 lg:px-8 lg:py-7" data-testid="subscription-panel">
      <div className="card-surface rounded-2xl p-4 mb-4 flex items-center justify-between">
        <div>
          <div className="text-[11px] uppercase tracking-widest" style={{ color: "var(--text-muted)" }}>Current plan</div>
          <div className="font-serif-display text-2xl capitalize gold-text">{currentTier}</div>
        </div>
        <Check size={22} className="gold-text" />
      </div>
      <div className="text-xs uppercase tracking-widest mb-3" style={{ color: "var(--text-muted)" }}>Change plan</div>
      <PlanSelector onSelect={select} submitting={submitting} ctaLabel="Update plan" initialTier={currentTier} />

      <StripeCheckoutSheet open={!!checkout} clientSecret={checkout?.client_secret} sessionId={checkout?.session_id}
        onClose={() => setCheckout(null)}
        onPaid={async () => { setCheckout(null); toast.success("Plan updated!"); await refreshUser(); }} />
    </motion.div>
  );
}

function AdminPanel({ refreshUser }) {
  const [data, setData] = useState(null);
  const [name, setName] = useState("");
  const [dues, setDues] = useState("");
  const [duesAmount, setDuesAmount] = useState("");
  const [duesFreq, setDuesFreq] = useState("monthly");
  const [duesDay, setDuesDay] = useState(1);
  const [saving, setSaving] = useState(false);
  const [roleBusy, setRoleBusy] = useState(null);

  const changeRole = async (uid, role) => {
    setRoleBusy(uid);
    try {
      await api.patch(`/families/members/${uid}/role`, { role });
      toast.success("Role updated");
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setRoleBusy(null); }
  };

  const load = useCallback(async () => {
    try {
      const res = await api.get("/family/settings");
      setData(res.data); setName(res.data.name);
      setDues(res.data.annual_dues != null ? String(res.data.annual_dues) : "");
      setDuesAmount(res.data.dues_amount ? String(res.data.dues_amount) : "");
      setDuesFreq(res.data.dues_frequency || "monthly");
      setDuesDay(res.data.dues_day || 1);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/family/settings", {
        name: name.trim(), annual_dues: dues ? parseFloat(dues) : 0,
        dues_amount: duesAmount ? parseFloat(duesAmount) : 0,
        dues_frequency: duesFreq, dues_day: parseInt(duesDay),
      });
      toast.success("Family settings saved");
      await load(); await refreshUser();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  if (!data) return <div className="grid place-items-center py-16"><Loader2 className="animate-spin gold-text" /></div>;

  const inputStyle = { background: "var(--bg-surface)", border: "1px solid var(--border)", color: "var(--text-primary)" };

  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="px-5 py-5 lg:px-8 lg:py-7 space-y-5 lg:space-y-6" data-testid="admin-panel">
      <div className="flex items-center gap-2 text-xs" style={{ color: "var(--text-muted)" }}>
        <ShieldCheck size={14} className="gold-text" /> Admin & Organizer controls
      </div>

      <div>
        <div className="text-xs uppercase tracking-widest mb-2" style={{ color: "var(--text-muted)" }}>Family name</div>
        <input value={name} onChange={(e) => setName(e.target.value)} data-testid="admin-family-name"
          className="w-full rounded-xl px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-[var(--accent)]" style={inputStyle} />
      </div>

      <div className="card-surface rounded-2xl p-4 space-y-3">
        <div className="text-xs uppercase tracking-widest flex items-center gap-1.5" style={{ color: "var(--text-muted)" }}>
          <DollarSign size={12} className="gold-text" /> Family dues (charged to every member)
        </div>
        <div>
          <label className="text-[11px]" style={{ color: "var(--text-muted)" }}>Amount (USD)</label>
          <input type="number" min="0" step="0.01" value={duesAmount} onChange={(e) => setDuesAmount(e.target.value)} data-testid="admin-dues-amount"
            placeholder="0.00" className="w-full mt-1 rounded-xl px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-[var(--accent)]" style={inputStyle} />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-[11px]" style={{ color: "var(--text-muted)" }}>Frequency</label>
            <select value={duesFreq} onChange={(e) => setDuesFreq(e.target.value)} data-testid="admin-dues-frequency"
              className="w-full mt-1 rounded-xl px-3 py-3 text-sm outline-none" style={inputStyle}>
              <option value="monthly">Monthly</option>
              <option value="quarterly">Quarterly</option>
              <option value="annual">Annual</option>
            </select>
          </div>
          <div>
            <label className="text-[11px]" style={{ color: "var(--text-muted)" }}>Charge day</label>
            <select value={duesDay} onChange={(e) => setDuesDay(e.target.value)} data-testid="admin-dues-day"
              className="w-full mt-1 rounded-xl px-3 py-3 text-sm outline-none" style={inputStyle}>
              <option value={1}>1st</option>
              <option value={15}>15th</option>
              <option value={30}>30th</option>
            </select>
          </div>
        </div>
        {duesAmount > 0 && (
          <div className="text-[11px]" style={{ color: "var(--text-secondary)" }}>
            Members are charged <b className="gold-text">${(parseFloat(duesAmount) * 1.05).toFixed(2)}</b> {duesFreq} (${parseFloat(duesAmount).toFixed(2)} dues + 5% platform fee).
          </div>
        )}
      </div>

      <motion.button whileTap={{ scale: 0.97 }} onClick={save} disabled={saving} data-testid="admin-save-btn"
        className="w-full rounded-xl py-3 font-semibold text-sm flex items-center justify-center gap-2" style={{ background: "var(--accent)", color: "#fff" }}>
        {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} Save changes
      </motion.button>

      <div>
        <div className="text-xs uppercase tracking-widest mb-2 flex items-center gap-1.5" style={{ color: "var(--text-muted)" }}>
          <Users size={12} className="gold-text" /> Family members ({data.members.length})
        </div>
        <div className="space-y-2 lg:space-y-0 lg:grid lg:grid-cols-2 lg:gap-3">
          {data.members.map((m) => (
            <div key={m.user_id} className="card-surface rounded-2xl p-3.5 flex items-center justify-between gap-2" data-testid={`admin-member-${m.user_id}`}>
              <div className="min-w-0">
                <div className="text-sm font-semibold truncate">{m.name}</div>
                <div className="text-[11px]" style={{ color: "var(--text-muted)" }}>
                  {m.age != null ? `${m.age} yrs · ` : ""}Member for {m.tenure} · {m.plan}
                </div>
              </div>
              <div className="relative shrink-0">
                {roleBusy === m.user_id
                  ? <Loader2 size={16} className="animate-spin gold-text" />
                  : <select value={m.role} onChange={(e) => changeRole(m.user_id, e.target.value)} data-testid={`admin-member-role-${m.user_id}`}
                      className="appearance-none rounded-full pl-3 pr-7 py-1.5 text-[11px] font-semibold uppercase tracking-wider gold-text outline-none cursor-pointer"
                      style={{ background: "color-mix(in srgb, var(--accent) 16%, transparent)", border: "1px solid color-mix(in srgb, var(--accent) 30%, transparent)" }}>
                      <option value="user">Standard</option>
                      <option value="organizer">Organizer</option>
                      <option value="admin">Admin</option>
                    </select>}
                {roleBusy !== m.user_id && <ChevronRight size={13} className="absolute right-2 top-2 rotate-90 pointer-events-none gold-text" />}
              </div>
            </div>
          ))}
        </div>
      </div>
    </motion.div>
  );
}
